import { getTeamsPublic, login } from '../../api/rpc';
import { toRpcError } from '../../api/errors';
import type { LoginSuccess, TeamPublic } from '../../api/types';
import { session } from '../../core/session';
import { button, createScreen, createSheet, h } from './common';

export const LOGIN_ERRORS = {
  invalid_team_or_pin: 'チームかPINが違います',
  too_many_attempts: 'しばらく待ってから入れ直してください',
  network: '通信できませんでした',
} as const;

/** ログインの失敗を画面の文言にする（推測：想定外は通信の文言にそろえる） */
export function loginErrorText(code: string): string {
  if (code === 'invalid_team_or_pin') return LOGIN_ERRORS.invalid_team_or_pin;
  if (code === 'too_many_attempts') return LOGIN_ERRORS.too_many_attempts;
  return LOGIN_ERRORS.network;
}

/** サーバーごとに並べる。サーバー名は画面に出さない（間隔だけで分ける） */
export function groupByServer(teams: TeamPublic[]): TeamPublic[][] {
  const m = new Map<string, TeamPublic[]>();
  for (const t of teams) m.set(t.server_id, [...(m.get(t.server_id) ?? []), t]);
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, g]) => g.sort((a, b) => a.team_name.localeCompare(b.team_name, 'ja')));
}

const HEX = /^#[0-9a-fA-F]{3,8}$/;

/**
 * 推測：ログイン画面の見た目は未確定。机の上の紙（耳付き）に、チームを選ぶ札とPINの欄を置いた最小の形。
 */
export async function showLogin(root: HTMLElement, eventId: string): Promise<LoginSuccess> {
  const screen = createScreen(root);
  const { wrap, sheet } = createSheet(701);
  screen.appendChild(wrap);
  sheet.appendChild(h('h1', undefined, 'チームを選んでください'));

  const list = h('div');
  list.style.cssText = 'display:flex;flex-direction:column;gap:18px;margin-bottom:16px';
  sheet.appendChild(list);

  const pinLabel = h('label', 'sh-field');
  pinLabel.append(document.createTextNode('PIN（4けた）'));
  const pin = h('input', 'sh-pin');
  pin.type = 'password';
  pin.inputMode = 'numeric';
  pin.maxLength = 4;
  pin.autocomplete = 'off';
  pin.pattern = '[0-9]{4}';
  pinLabel.appendChild(pin);
  sheet.appendChild(pinLabel);

  const err = h('p', 'sh-err');
  err.setAttribute('role', 'alert');
  sheet.appendChild(err);

  let chosen: string | null = null;
  let busy = false;
  let resolveDone!: (r: LoginSuccess) => void;
  const done = new Promise<LoginSuccess>((res) => {
    resolveDone = res;
  });
  async function submit(): Promise<void> {
    if (busy || !chosen || !/^\d{4}$/.test(pin.value)) return;
    busy = true;
    err.textContent = '';
    refresh();
    try {
      const r = await login(chosen, pin.value, session.deviceId());
      if (r.ok) {
        session.set({
          token: r.token,
          team_id: r.team_id,
          team_name: r.team_name,
          server_id: r.server_id,
          event_id: r.event_id,
          terms_version: r.terms_version,
          terms_accepted: r.terms_accepted,
        });
        screen.remove();
        resolveDone(r);
        return;
      }
      err.textContent = loginErrorText(r.error);
    } catch (e) {
      err.textContent = loginErrorText(toRpcError(e).code);
    }
    pin.value = '';
    busy = false;
    refresh();
  }
  const choices: { id: string; el: HTMLButtonElement }[] = [];
  const go = button('入る', 'ink', () => void submit());
  const refresh = (): void => {
    go.setAttribute('aria-disabled', chosen && /^\d{4}$/.test(pin.value) && !busy ? 'false' : 'true');
  };
  pin.addEventListener('input', () => {
    pin.value = pin.value.replace(/\D/g, '').slice(0, 4);
    refresh();
  });
  screen.appendChild(go);
  refresh();

  async function loadTeams(): Promise<void> {
    try {
      const teams = await getTeamsPublic(eventId);
      list.replaceChildren();
      choices.length = 0;
      for (const g of groupByServer(teams)) {
        const row = h('div');
        row.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:8px';
        for (const t of g) {
          const b = h('button', 'sh-btn sh-btn-paper');
          b.type = 'button';
          b.style.cssText = 'min-height:48px;padding:0 8px;gap:8px;font-size:16px';
          const dot = h('span');
          dot.style.cssText = 'width:12px;height:12px;border-radius:50%;flex:none;border:1px solid var(--edge)';
          if (t.color && HEX.test(t.color)) dot.style.background = t.color;
          b.append(dot, h('span', undefined, t.team_name));
          b.setAttribute('aria-pressed', 'false');
          b.addEventListener('click', () => {
            chosen = t.team_id;
            for (const c of choices) {
              const on = c.id === t.team_id;
              c.el.setAttribute('aria-pressed', String(on));
              c.el.style.boxShadow = on ? 'inset 0 0 0 3px var(--ink)' : '';
            }
            refresh();
          });
          choices.push({ id: t.team_id, el: b });
          row.appendChild(b);
        }
        list.appendChild(row);
      }
    } catch (e) {
      err.textContent = loginErrorText(toRpcError(e).code);
      const retry = button('もう一度読み込む', 'paper', () => {
        retry.remove();
        err.textContent = '';
        void loadTeams();
      });
      list.replaceChildren(retry);
    }
  }
  void loadTeams();

  return done;
}
